import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SCROLL_THRESHOLD_DEFAULT = 600;

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolledPastHero, setScrolledPastHero] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [isProjectDetailPage, setIsProjectDetailPage] = useState(false);

  const { t, i18n } = useTranslation();

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  // ------------------ Toggle lingua con persistenza ------------------
  const toggleLanguage = () => {
    const newLang = i18n.language === "it" ? "en" : "it";
    i18n.changeLanguage(newLang);
    localStorage.setItem("lang", newLang);
  };

  // ------------------- Rileva se siamo in ProjectDetail -------------------
  useEffect(() => {
    const onProjectPage = location.pathname.startsWith("/progetti/");
    setIsProjectDetailPage(onProjectPage);
    setIsMenuOpen(false); // chiude il menu ad ogni cambio pagina
  }, [location]);

  // ------------------- Gestione scroll -------------------
  useEffect(() => {
    const handleScroll = () => {
      if (isProjectDetailPage) {
        setScrolledPastHero(true);
      } else {
        setScrolledPastHero(window.scrollY > SCROLL_THRESHOLD_DEFAULT);
      }
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isProjectDetailPage]);

  // ------------------- Gestione click su link -------------------
  const handleNavLinkClick = (section) => {
    setIsMenuOpen(false);
    if (location.pathname === "/") {
      document.getElementById(section)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${section}`, { replace: false });
      setTimeout(() => {
        document.getElementById(section)?.scrollIntoView({ behavior: "smooth" });
      }, 50);
    }
  };

  // ------------------- Navbar classi -------------------
  const navbarClasses = `fixed top-0 left-0 w-full z-40 transition-all duration-300 ${
    isProjectDetailPage
      ? "shadow-lg backdrop-blur-sm bg-black bg-opacity-90"
      : scrolledPastHero
        ? "shadow-lg backdrop-blur-sm bg-black bg-opacity-90"
        : "bg-transparent"
  }`;

  return (
    <nav className={navbarClasses}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div
            className="flex flex-col leading-none cursor-pointer select-none"
            onClick={() => handleNavLinkClick("home")}
          >
            <span className="logo-vision">VISION</span>
            <span className="logo-design">DESIGN</span>
          </div>

          {/* Right side: lingua + hamburger */}
          <div className="flex items-center gap-3">
            {/* Bottone lingua */}
            <button
              onClick={toggleLanguage}
              className="
                text-xl font-semibold
                px-3 py-1 rounded-md
                text-white/80
                hover:text-white
                transition
                border border-white/20
                hover:border-white/40
              "
            >
              {i18n.language === "it" ? "EN" : "IT"}
            </button>

            {/* Hamburger */}
            <button
              className={`flex flex-col space-y-2 p-2 rounded transition-colors duration-200 cursor-pointer relative ${
                isMenuOpen ? "bg-gray-800 z-50" : "hover:bg-gray-800"
              }`}
              onClick={toggleMenu}
              aria-label={isMenuOpen ? t("nav.close") : t("nav.open")}
            >
              <span
                className={`block transition-all duration-300 ${
                  scrolledPastHero ? "w-9 h-1.5 bg-gradient-to-r from-blue-400 to-purple-400" : "w-8 h-1 bg-white"
                } ${isMenuOpen ? "rotate-45 translate-y-2.5 hover:brightness-150" : ""}`}
              />
              <span
                className={`block transition-all duration-300 ${
                  scrolledPastHero ? "w-7 h-1.5 bg-gradient-to-r from-blue-400 to-purple-400" : "w-6 h-1 bg-white"
                } ${isMenuOpen ? "opacity-0" : ""}`}
              />
              <span
                className={`block transition-all duration-300 ${
                  scrolledPastHero ? "w-9 h-1.5 bg-gradient-to-r from-blue-400 to-purple-400" : "w-8 h-1 bg-white"
                } ${isMenuOpen ? "-rotate-45 -translate-y-2.5 hover:brightness-150" : ""}`}
              />
            </button>
          </div>
        </div>

        {/* Menu Mobile / Desktop */}
        <div
          className={`py-20 my-4 fixed top-16 right-0 w-full z-40 transition-all duration-500 ease-in-out ${
            isMenuOpen
              ? window.innerWidth < 1100
                ? "bg-black bg-opacity-80 backdrop-blur-sm"
                : scrolledPastHero
                  ? "bg-black bg-opacity-80 backdrop-blur-sm"
                  : "bg-black bg-opacity-0"
              : "bg-black bg-opacity-0"
          } ${isMenuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0 overflow-hidden pointer-events-none"} pb-20`}
        >
          <div className="flex flex-col space-y-6 max-w-6xl px-4 pr-10 sm:pr-16 md:pr-24 items-end ml-auto">
            {["home", "about", "skills", "projects", "contacts"].map((section, i) => (
              <a
                key={section}
                href={`#${section}`}
                className={`nav-menu-link text-xl lg:text-2xl pl-6 group font-semibold
                  transition duration-900 ease-out
                  bg-gradient-to-r from-white to-white bg-clip-text text-transparent
                  hover:from-blue-300 hover:to-purple-400 hover:font-bold
                  ${isMenuOpen ? "opacity-100 translate-y-4" : "opacity-0 translate-y-2"}`}
                style={{ transitionDelay: isMenuOpen ? `${(i + 1) * 100}ms` : "0ms" }}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavLinkClick(section);
                }}
              >
                {t(`nav.${section}`)}
              </a>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
